import { useEffect, useRef, useCallback } from "react";

/**
 * Discreet audio + browser Notification helper.
 *
 *   const notify = useBookingNotifications({ boatName });
 *   notify(booking);              // 'new' booking → ascending chime + notification
 *   notify(booking, 'end');       // treatment finished → soft descending chime, no notification
 *   notify(booking, 'update');    // subtle single tick
 *
 * Sound is played via WebAudio with tiny envelopes so it is discreet, not jarring.
 * A single per-tab AudioContext is reused. The consumer can pass { muted: true } to
 * suppress the audio (used by the mute toggle in Spa Ops).
 */
export function useBookingNotifications({ appName = "Yacht Spa", boatName = "", muted = false } = {}) {
  const permissionRef = useRef(typeof Notification !== "undefined" ? Notification.permission : "unsupported");
  const ctxRef = useRef(null);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (permissionRef.current === "default") {
      try { Notification.requestPermission().then((p) => { permissionRef.current = p; }); } catch { /* Safari */ }
    }
  }, []);

  const ensureCtx = () => {
    if (ctxRef.current) return ctxRef.current;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    try { ctxRef.current = new Ctx(); } catch { ctxRef.current = null; }
    return ctxRef.current;
  };

  const playTone = useCallback((freqs, dur = 0.45) => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    const t0 = ctx.currentTime + 0.02;
    freqs.forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      const start = t0 + i * (dur * 0.55);
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.14, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + dur);
      o.connect(g); g.connect(ctx.destination);
      o.start(start); o.stop(start + dur + 0.05);
    });
  }, [muted]);

  return useCallback((booking = {}, kind = "new") => {
    // 1. Audio — different tone per event kind.
    if (kind === "new")       playTone([880, 1318.5], 0.42);          // A5 → E6, bright rising chime
    else if (kind === "end")  playTone([659.25, 440], 0.55);          // E5 → A4, gentle descending bell
    else if (kind === "update") playTone([1046.5], 0.20);             // C6 short tick
    else if (kind === "cancel") playTone([440, 349.23], 0.5);         // A4 → F4 soft descending
    else                      playTone([880], 0.30);

    // 2. Browser Notification — only for new bookings (avoid noise for end/update).
    if (kind !== "new") return;
    if (typeof Notification === "undefined" || permissionRef.current !== "granted") return;
    try {
      const when = new Date(booking.start_at || Date.now()).toLocaleString("en-US", {
        weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
      });
      const title = `New booking · ${booking.guest_name || "Guest"}`;
      const body = `${booking.treatment_name || "Treatment"} · ${when}${boatName ? " · " + boatName : ""}`;
      const n = new Notification(title, { body, tag: booking.id || String(Date.now()), icon: "/favicon.ico", silent: true });
      n.onclick = () => { window.focus(); n.close(); };
    } catch { /* ignore */ }
  }, [playTone, boatName]);
}
